import { Link, useLocation } from "react-router-dom";
import { useAuth } from "./AuthProvider";
import { ThemeToggle } from "./ThemeToggle";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  LayoutDashboard,
  Database,
  Users,
  Settings,
  LogOut,
  User,
  Menu,
  X,
  ClipboardList,
  PlaneTakeoff,
  Plane,
  Package,
} from "lucide-react";

import { cn } from "../lib/utils";
import { useState } from "react";
import { LiveTicker } from "./LiveTicker";

const navItems = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/fids-t1", label: "FIDS T1", icon: PlaneTakeoff },
  { path: "/fids-t2", label: "FIDS T2", icon: Plane },
  { path: "/fids-cargo", label: "FIDS Cargo", icon: Package },
  { path: "/flights", label: "Integration Data", icon: Database },
  { path: "/users", label: "Users", icon: Users, adminOnly: true },
  { path: "/audit-logs", label: "Audit Logs", icon: ClipboardList, adminOnly: true },
  { path: "/settings", label: "Settings", icon: Settings, adminOnly: true },
];

export function Layout({ children }) {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const filteredNavItems = navItems.filter(
    (item) => !item.adminOnly || isAdmin
  );

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-background/80 border-b border-border">
        <div className="w-full px-3 h-12 flex items-center">
          {/* Logo - flush left */}
          <Link
            to="/dashboard"
            className="flex items-center shrink-0 mr-4"
            data-testid="logo-link"
          >
            <span className="font-heading font-bold text-lg tracking-tight whitespace-nowrap">
              Cuvano® FlightCore
            </span>
          </Link>

          {/* Desktop Navigation - center */}
          <nav className="hidden lg:flex items-center gap-0.5 flex-1 justify-center">
            {filteredNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link key={item.path} to={item.path}>
                  <Button
                    variant={isActive ? "secondary" : "ghost"}
                    size="sm"
                    className={cn(
                      "gap-1.5 h-8 text-xs px-2.5",
                      isActive && "bg-primary/10 text-primary"
                    )}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {item.label}
                  </Button>
                </Link>
              );
            })}
          </nav>

          {/* Right side - flush right */}
          <div className="flex items-center gap-1.5 shrink-0 ml-auto">
            <LiveTicker />
            <ThemeToggle />

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-1.5 h-8"
                  data-testid="user-menu-trigger"
                >
                  <User className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline text-xs max-w-[140px] truncate">
                    {user?.email}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span className="font-medium text-sm">{user?.email}</span>
                    <span className="text-xs text-muted-foreground capitalize">
                      {user?.role}
                    </span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={logout}
                  className="text-destructive cursor-pointer"
                  data-testid="logout-btn"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden h-8 w-8"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-border bg-background">
            <nav className="px-3 py-2 flex flex-col gap-0.5">
              {filteredNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Button
                      variant={isActive ? "secondary" : "ghost"}
                      size="sm"
                      className={cn(
                        "w-full justify-start gap-2 h-9",
                        isActive && "bg-primary/10 text-primary"
                      )}
                    >
                      <Icon className="h-4 w-4" />
                      {item.label}
                    </Button>
                  </Link>
                );
              })}
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="border-t border-border py-4">
        <p className="text-zinc-500 text-[10px] tracking-wider text-center">
          POWERED BY CUVANO®
        </p>
      </footer>
    </div>
  );
}
